import React from "react";
import classNames from "classnames";

const VARIANT_CLASS = {
  primary: "cv-btn-primary",
  secondary: "cv-btn-secondary",
  ghost: "cv-btn-ghost",
  danger: "cv-btn-danger",
  success: "cv-btn-success",
  // R-11 — navigation items: the 44px floor and focus behaviour, no border, no fill or text
  // colour (the consuming shell supplies those — every variant palette above is a light-surface
  // palette and would be wrong on the dark admin rail).
  //
  // TWO of them, because a nav item has an AXIS and the axis changes what "aligned" means. R-11
  // shipped one variant with `justify-start`, which is correct for a horizontal row (icon then
  // label, read from the inline start) and wrong for a stacked rail item (icon ABOVE label), so
  // the admin rail overrode its own variant at the call site to get the column back. A variant
  // that has to be un-done by its only consumer is not a variant; it is a default with extra
  // steps. Split, so each axis is a thing you SELECT rather than a thing you correct.
  nav: "cv-btn-nav",
  "nav-stacked": "cv-btn-nav-stacked",
};

const SIZE_CLASS = {
  sm: "px-2.5 py-1.5 text-xs",
  md: "px-3 py-2 text-sm",
  lg: "px-4 py-2.5 text-sm",
};

/**
 * The nav variants own their own box and take NO size.
 *
 * This is forced, not stylistic. SIZE_CLASS's padding is a UTILITY, and Tailwind emits `px-3`
 * after `px-1`, so neither a component-layer `@apply px-1` nor a later-listed `px-1` utility can
 * beat it — measured in DS-1(D), where the rail's plain `px-1` was INERT (padding stayed 12px,
 * invisible in English, and pushed the Hebrew label to three lines and the item to 80px) and only
 * `!px-1` was seen at all. The only honest fix is to not emit a size for a control whose box is
 * not a point on the action scale: a rail item is sized by the rail, not by sm/md/lg.
 *
 * `size` is therefore NOT part of the nav contract. The fence asserts this rather than trusting
 * the comment — a prop that is silently ignored is the divergence shape this arc keeps finding.
 */
const SIZELESS_VARIANTS = new Set(["nav", "nav-stacked"]);

/**
 * Button — the design system's action control.
 *
 * `asChild` (R-9) merges the button's classes onto the single child ELEMENT instead of
 * rendering a <button>. It exists because the day-one paths are NAVIGATION, and navigation is
 * <Link>/<NavLink>: without it the first control a new teacher must press stays a 12px text
 * link, because a <button> cannot be a route.
 *
 * RENDERING ONLY. It adds no variant, no size, no icon-only affordance and no loading state —
 * those are separate proposals with separate rulings. A component grown to fit every call site
 * without a ruling is how a design system becomes a junk drawer.
 *
 * `type` is NOT forwarded to a child. It is a <button> attribute; putting type="button" on an
 * <a> is invalid HTML and React would warn.
 */
export function Button({
  variant = "primary",
  size = "md",
  fullWidth = false,
  className,
  type = "button",
  asChild = false,
  children,
  ...props
}) {
  const classes = classNames(
    "cv-btn",
    VARIANT_CLASS[variant] || VARIANT_CLASS.primary,
    !SIZELESS_VARIANTS.has(variant) && (SIZE_CLASS[size] || SIZE_CLASS.md),
    fullWidth && "w-full",
    className
  );

  if (asChild) {
    const child = React.Children.only(children);
    const childClassName = child.props.className;

    // NavLink's className may be a FUNCTION of ({ isActive, isPending }) — that function IS the
    // active-state mechanism. Flattening it to a string here would silently delete the active
    // state while everything still looked right, which is this arc's recurring failure shape.
    // So a function child className stays a function, and the button classes compose into it.
    const mergedClassName =
      typeof childClassName === "function"
        ? (state) => classNames(classes, childClassName(state))
        : classNames(classes, childClassName);

    return React.cloneElement(child, { ...props, className: mergedClassName });
  }

  return <button type={type} className={classes} {...props}>{children}</button>;
}
